
import React, { useState, useEffect } from 'react';
import { Company, PostgresConfig, User } from '../types';
import { getPostgresClient } from '../services/postgresClient';
import { normalizeSettingsTabsFromDb } from '../utils/permissions';
import {
  MAX_LOGIN_ATTEMPTS,
  getRemainingLockoutTime,
  getStoredAttempts,
  isLockedOut,
  recordFailedAttempt,
  recordSuccessfulAttempt,
} from '../utils/authLockout';

interface AuthModalProps {
  company: Company;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: User) => void;
  /** Alinhado ao `getSB()` / health — mesma origem que o restante do app */
  postgresConfig?: PostgresConfig;
}

export const AuthModal: React.FC<AuthModalProps> = ({ company, isOpen, onClose, onSuccess, postgresConfig }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(0);

  // Atualiza o tempo de bloqueio a cada segundo
  useEffect(() => {
    if (!isOpen) return;

    const updateLockout = () => {
      if (isLockedOut(company)) {
        setLockoutTime(getRemainingLockoutTime(company));
        setError(`Muitas tentativas. Tente novamente em ${Math.ceil(getRemainingLockoutTime(company) / 1000 / 60)} minutos.`);
      } else {
        setLockoutTime(0);
        if (error.includes('Muitas tentativas')) {
          setError('');
        }
      }
    };

    updateLockout();
    const interval = setInterval(updateLockout, 1000);
    return () => clearInterval(interval);
  }, [isOpen, company]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Verifica rate limiting
    if (isLockedOut(company)) {
      const remainingMinutes = Math.ceil(getRemainingLockoutTime(company) / 1000 / 60);
      setError(`Muitas tentativas falhas. Aguarde ${remainingMinutes} minutos.`);
      return;
    }

    setIsLoading(true);

    try {
      const postgres = getPostgresClient(postgresConfig?.url);
      const loginUsername = username.trim().toLowerCase();

      // 1. Tentar fazer Login via Autenticação Segura (JWT)
      const { data, error: authError } = await postgres.auth.signInWithPassword({
        email: loginUsername,
        password: password
      });

      if (authError || !data.user) {
        recordFailedAttempt(company);
        const remainingAttempts = MAX_LOGIN_ATTEMPTS - getStoredAttempts(company).count;
        if (remainingAttempts <= 0) {
          setError('Conta bloqueada por 5 minutos devido a muitas tentativas.');
        } else {
          setError(`Credenciais inválidas. Tentativas restantes: ${remainingAttempts}`);
        }
        return;
      }

      // 2. Agora que o cliente está autenticado na nuvem, podemos ler as regras na tabela 'rh_users'
      const { data: userRow, error: uErr } = await postgres
        .from('rh_users')
        .select('id, username, perm_dashboard, perm_hr, perm_settings, perm_absences, perm_employee_create, perm_employee_edit, perm_employee_delete, perm_settings_detail')
        .eq('username', loginUsername)
        .maybeSingle();

      if (uErr || !userRow) {
        setError('Painel sem permissão configurada para o acesso.');
        return;
      }

      // Processar empresas permitidas
      const { data: links } = await postgres
        .from('rh_user_companies')
        .select('company_id')
        .eq('user_id', userRow.id);

      const allowedComps: string[] = [];
      for (const link of links ?? []) {
        const { data: company } = await postgres
          .from('rh_companies')
          .select('name')
          .eq('id', (link as any).company_id)
          .maybeSingle();
        if ((company as any)?.name) {
          allowedComps.push((company as any).name);
        }
      }

      if (allowedComps.includes(company)) {
        // Limpa tentativas falhas após login bem-sucedido
        recordSuccessfulAttempt(company);
        onSuccess({
          id: userRow.id,
          username: userRow.username,
          permissions: {
            dashboard: userRow.perm_dashboard,
            hr: userRow.perm_hr,
            settings: userRow.perm_settings,
            absences: userRow.perm_absences,
            employeeCreate: (userRow as { perm_employee_create?: boolean }).perm_employee_create !== false,
            employeeEdit: (userRow as { perm_employee_edit?: boolean }).perm_employee_edit !== false,
            employeeDelete: (userRow as { perm_employee_delete?: boolean }).perm_employee_delete !== false,
            settingsTabs: normalizeSettingsTabsFromDb(
              userRow.perm_settings,
              (userRow as { perm_settings_detail?: unknown }).perm_settings_detail,
            ),
          },
          allowedCompanies: allowedComps as Company[]
        });
      } else {
        recordFailedAttempt(company);
        setError(`Acesso negado: você não tem permissão para a unidade ${company}.`);
      }
    } catch (err) {
      console.error('[AuthModal] Erro inesperado no login:', err);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('Failed to fetch') || msg.includes('NetworkError') || msg.includes('Network request failed')) {
        setError('Não foi possível conectar ao servidor. Verifique se a API está no ar e a URL nas configurações.');
      } else {
        setError('Erro ao validar acesso remoto.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xl animate-in fade-in duration-300">
      <div className="bg-white rounded-xl shadow-[0_32px_120px_-20px_rgba(0,0,0,0.3)] w-full max-w-sm overflow-hidden flex flex-col border border-white/40">
        <div className="p-6 text-center border-b border-slate-50">
          <div className="size-12 rounded-xl bg-primary-accent/5 flex items-center justify-center mx-auto mb-3">
            <span className="material-symbols-outlined !text-[24px] text-primary-accent">lock</span>
          </div>
          <h3 className="text-base font-bold text-slate-800">Acessar {company}</h3>
          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Identifique-se para continuar</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-[11px] text-red-600 font-medium text-center animate-in shake duration-300">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1.5 ml-1">Usuário</label>
              <input
                autoFocus
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border-none text-xs focus:ring-2 focus:ring-primary-accent transition-all"
                placeholder="Login (ex: joao.silva)"
              />
            </div>
            <div>
              <label className="block text-[9px] font-bold text-slate-400 uppercase mb-1.5 ml-1">Senha</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border-none text-xs focus:ring-2 focus:ring-primary-accent transition-all"
                placeholder="••••••••"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2.5 pt-2">
            <button
              type="submit"
              disabled={isLoading || lockoutTime > 0}
              className="w-full py-3 bg-primary-accent text-white text-xs font-bold rounded-xl shadow-xl shadow-primary-accent/20 hover:brightness-110 active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="h-3.5 w-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : null}
              {isLoading ? 'Verificando...' : lockoutTime > 0 ? 'Bloqueado' : 'Entrar no Sistema'}
            </button>

            <div className="text-center">
              <p className="text-[9px] text-slate-300 font-medium">Acesso seguro — Credenciais criptografadas</p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="w-full py-1.5 text-slate-400 text-[9px] font-bold uppercase tracking-widest hover:text-slate-600 transition-colors"
            >
              Voltar à Seleção
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
